import { useEffect } from 'react';
import { FormikProps, useFormik } from 'formik';
import * as Yup from 'yup';

import { IZone } from 'models';
import { useZone } from 'pages/zones/zones.hook';
import { useApproveDecalMutation, useBulkApproveDecalsMutation } from 'store/apis/decal.api';
import { selectCurrentUserInfo } from 'store/slices/auth.slice';
import { useAppSelector } from 'store/store.hook';
import { DEFAULT_DECAL_VERSION, EDecalVersion } from 'enums';

interface UseDecalFormManagerProps {
  decalId: number; // zone.id
  version?: EDecalVersion;
  next?: (data?: any) => void;
  zoneIdsToApprove: number[];
}

export const useDecalFormManager = ({
  decalId: id,
  version = DEFAULT_DECAL_VERSION,
  next = () => {},
  zoneIdsToApprove = [],
}: UseDecalFormManagerProps) => {
  const { orgSlug } = useAppSelector(selectCurrentUserInfo);
  const [loading_zone, zone] = useZone({ zoneId: id });
  const [approveSingle, singleResult] = useApproveDecalMutation();
  const [approveBulk, bulkResult] = useBulkApproveDecalsMutation();

  const isBulk = zoneIdsToApprove.length > 0;
  const { isLoading, isSuccess, isError, data } = isBulk ? bulkResult : singleResult;
  const initialValues = {};

  const validationSchema = Yup.object({});

  const formManager = useFormik({
    initialValues,
    validationSchema,
    onSubmit: () => {
      if (isBulk) {
        approveBulk({ orgSlug, zone_ids: zoneIdsToApprove });
      } else {
        approveSingle({ orgSlug, id, version });
      }
    },
  });

  useEffect(() => {
    if (!isLoading && isSuccess && !isError) {
      next(data);
    }
  }, [isLoading, isSuccess, isError]);

  return [
    {
      zone,
      formManager,
      loading_form: loading_zone,
      loading_submit: isLoading,
    },
  ] as [
    {
      zone: IZone;
      formManager: FormikProps<typeof initialValues>;
      loading_form: boolean;
      loading_submit: boolean;
    },
  ];
};
